import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  RootContainer,
  DropZone,
  Gallery,
  AddMoreButton,
  FilePreviewGrid, FileActionsOverlay,
} from '@securspace/securspace-ui-kit';
import type { StagedFile, ValidationError } from '@securspace/securspace-ui-kit';
import { IconButton, styled } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CropIcon from '@mui/icons-material/Crop';
import Delete from '@mui/icons-material/Delete';
import ImagePreviewModal, {type ImagePreviewAction} from '../../../components/imageUpload/ImagePreviewModal';
import CropPanel from '../../../components/imageUpload/CropPanel';
import { useSnackbar } from '../../../context/snackbar-context';

/** Accepted upload MIME types — mirrors the legacy uploader (PNG, JPG, GIF). */
const ACCEPTED_FILE_TYPES = ['image/png', 'image/jpeg', 'image/gif'];

/** 20 MB — matches the legacy uploader's limit (UIkit does not enforce size). */
const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024;

const DROP_PROMPT = 'Drag and drop a file, or click to select a file';

/**
 * Reusable styled icon button for image actions (edit/remove) with white border and circular shape.
 */
const ActionIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.common.white,
  border: '2px solid #fff',
  borderRadius: '50%',
}));

export interface ExistingImage {
  fileName: string;
  url: string;
}

interface WizardImageUploadProps {
  /** Allow selecting multiple files (gallery) vs a single file (listing image). */
  multiple: boolean;
  /** Currently selected (not-yet-uploaded) files. */
  files: File[];
  onFilesChange: (files: File[]) => void;
  /** Maximum number of images total (existing + newly-selected). */
  maxFiles?: number;
  /** Already-persisted images to show as read-only previews (edit mode). */
  existingImages?: ExistingImage[];
  /** Called when the user removes an already-persisted image. Omit to keep
   *  existing images read-only (no trash icon shown for them). */
  onRemoveExisting?: (image: ExistingImage) => void;
}

/**
 * Best-effort MIME type from a stored file name, used only to build the
 * placeholder `File` for an already-persisted image so the UIkit grid can
 * render its thumbnail. The bytes are never read or uploaded.
 */
const inferTypeFromFileName = (fileName: string): string => {
  const ext = fileName.split('.').pop()?.toLowerCase();
  if (ext === 'png') return 'image/png';
  if (ext === 'gif') return 'image/gif';
  if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
  return '';
};

/**
 * Drag-and-drop image upload zone backed by the securspace-ui-kit `ImageUpload`
 * components. Used for both the Listing Image (single) and the Image Gallery
 * (multiple) sections of the Create/Edit Location wizard.
 *
 * The caller owns the not-yet-uploaded files as a plain `File[]`; this wrapper
 * bridges that to the UIkit's caller-owned `StagedFile[]` model (owning each
 * `previewUrl` and its object-URL lifecycle) and seeds any already-persisted
 * images as read-only thumbnails.
 *
 * Rejects files over 20 MB or of an unsupported type with a toast, and caps the
 * number of selected files when `maxFiles` is provided.
 */
const WizardImageUpload = (props: WizardImageUploadProps) => {
  const { multiple, files, onFilesChange, maxFiles, existingImages = [], onRemoveExisting } = props;
  const { snackbarShowMessage } = useSnackbar();

  // Caller owns the preview modal's open/close state (per the UIKit contract):
  // the pencil action stages a file here; the modal renders while it is set.
  const [previewingFile, setPreviewingFile] = useState<StagedFile | null>(null);
  const previewingFileRef = useRef<StagedFile | null>(null);
  const [actionPreviewOverrides, setActionPreviewOverrides] = useState<Map<File, string>>(
    () => new Map()
  );
  const ownedPreviewUrlsRef = useRef<Set<string>>(new Set());
  const pendingRevocationsRef = useRef<string[]>([]);

  // NOTE: Crop is intentionally NOT exposed in the wizard. This array (and the
  // supporting handleActionComplete/actionPreviewOverrides/pendingRevocationsRef
  // plumbing below) is kept dormant/unused so the crop action can be re-enabled
  // here later without re-deriving the update-before-revoke ordering. Cropping
  // is currently only user-facing in the Check-In form's ImageUploadSection.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const previewActions = useMemo<ImagePreviewAction[]>(() => [
    {
      id: 'crop',
      label: 'Crop',
      icon: <CropIcon/>,
      panel: (file, onComplete, onCancel, onError) => (
        <CropPanel
          file={file}
          onComplete={onComplete}
          onCancel={onCancel}
          onError={onError}
        />
      ),
    },
  ], []);

  useEffect(() => {
    previewingFileRef.current = previewingFile;
  }, [previewingFile]);

  // Build object-URL previews for newly-added files, keyed by File identity so a
  // URL persists across renders unless its File actually changes. This prevents
  // revoking a blob URL that a thumbnail — or an open preview modal — is still
  // displaying (the `files` prop array identity may change on every parent render).
  const previewUrlsRef = useRef<Map<File, string>>(new Map());
  const [newPreviews, setNewPreviews] = useState<StagedFile[]>([]);

  useEffect(() => {
    const cache = previewUrlsRef.current;
    const next: StagedFile[] = files.map((file) => {
      let previewUrl = cache.get(file);
      if (!previewUrl) {
        previewUrl = URL.createObjectURL(file);
        cache.set(file, previewUrl);
        ownedPreviewUrlsRef.current.add(previewUrl);
      }
      return { file, previewUrl };
    });
    // Revoke and forget URLs for files that are no longer present.
    cache.forEach((previewUrl, file) => {
      if (!files.includes(file)) {
        if (ownedPreviewUrlsRef.current.delete(previewUrl)) {
          URL.revokeObjectURL(previewUrl);
        }
        cache.delete(file);
      }
    });
    setNewPreviews(next);
  }, [files]);

  // Revoke every remaining object URL on unmount.
  useEffect(
    () => () => {
      ownedPreviewUrlsRef.current.forEach((previewUrl) => URL.revokeObjectURL(previewUrl));
      ownedPreviewUrlsRef.current.clear();
      previewUrlsRef.current.clear();
    },
    []
  );

  // Action completion first commits the replacement URL to the modal and grid.
  // This effect runs after that paint and only then revokes the superseded URL.
  useEffect(() => {
    const pendingUrls = pendingRevocationsRef.current.splice(0);
    pendingUrls.forEach((previewUrl) => {
      if (ownedPreviewUrlsRef.current.delete(previewUrl)) {
        URL.revokeObjectURL(previewUrl);
      }
    });
  });

  // An already-persisted image only has a file name + hosted URL, but the UIKit's
  // `StagedFile` needs a `File` and a `previewUrl`. So for each existing image we make a
  // placeholder `File` (used just as a stand-in) and use its hosted URL as the `previewUrl`.
  //
  // `byFile` maps each placeholder back to its `ExistingImage` so that removing a thumbnail
  // can look up which persisted image to delete. Placeholders are recreated whenever
  // `existingImages` changes, but this memo keeps them stable for the current render.
  const { existingStagedBase, existingImageByFile } = useMemo(() => {
    const byFile = new Map<File, ExistingImage>();
    const staged: StagedFile[] = existingImages.map((image) => {
      const file = new File([], image.fileName, { type: inferTypeFromFileName(image.fileName) });
      byFile.set(file, image);
      return { file, previewUrl: image.url };
    });
    return { existingStagedBase: staged, existingImageByFile: byFile };
  }, [existingImages]);

  const existingStaged = useMemo(
    () => existingStagedBase.map((file) => ({
      ...file,
      previewUrl: actionPreviewOverrides.get(file.file) ?? file.previewUrl,
    })),
    [existingStagedBase, actionPreviewOverrides]
  );

  // In single (Listing Image) mode a newly-added file supersedes any existing
  // saved image, so the one slot never shows two thumbnails in edit mode.
  const stagedFiles: StagedFile[] = useMemo(
    () =>
      !multiple && newPreviews.length > 0
        ? newPreviews
        : [...existingStaged, ...newPreviews],
    [multiple, existingStaged, newPreviews]
  );

  const handleFilesAdded = (added: File[]) => {
    if (!added || added.length === 0) return;

    const withinSizeLimit = added.filter((file) => file.size <= MAX_FILE_SIZE_BYTES);
    const oversizedCount = added.length - withinSizeLimit.length;
    if (oversizedCount > 0) {
      snackbarShowMessage(
        oversizedCount === 1
          ? 'That image is too large. Please choose a file under 20 MB.'
          : `${oversizedCount} images are too large. Please choose files under 20 MB.`,
        'warning'
      );
    }
    if (withinSizeLimit.length === 0) return;

    if (!multiple) {
      onFilesChange(withinSizeLimit.slice(0, 1));
      return;
    }
    onFilesChange([...files, ...withinSizeLimit]);
  };

  const handleValidationFailed = (error: ValidationError) => {
    if (error.code === 'INVALID_FILE_TYPE') {
      snackbarShowMessage('Unsupported file type. Please upload a PNG, JPG or GIF image.', 'warning');
    } else if (error.code === 'MAX_FILES_EXCEEDED' && maxFiles) {
      snackbarShowMessage(`You can add up to ${maxFiles} images total.`, 'warning');
    } else {
      snackbarShowMessage(error.message, 'warning');
    }
  };

  const handlePreview = (file: StagedFile) => {
    previewingFileRef.current = file;
    setPreviewingFile(file);
  };

  // Dormant — see the note above `previewActions`. Kept, but no longer wired
  // to `ImagePreviewModal`'s `onActionComplete` (it defaults to a no-op since
  // no `actions` are passed), so this is unreachable until crop is re-enabled.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const handleActionComplete = (updated: StagedFile) => {
    const currentFile = previewingFileRef.current;
    if (!currentFile || currentFile.file !== updated.file) return;

    const oldUrl = currentFile.previewUrl;
    previewingFileRef.current = updated;
    setPreviewingFile(updated);
    ownedPreviewUrlsRef.current.add(updated.previewUrl);

    if (previewUrlsRef.current.has(updated.file)) {
      previewUrlsRef.current.set(updated.file, updated.previewUrl);
      setNewPreviews((current) => current.map((file) =>
        file.file === updated.file ? updated : file
      ));
    } else {
      setActionPreviewOverrides((current) => {
        const next = new Map(current);
        next.set(updated.file, updated.previewUrl);
        return next;
      });
    }

    if (oldUrl !== updated.previewUrl) {
      pendingRevocationsRef.current.push(oldUrl);
    }
  };
  void handleActionComplete; // dormant — see note above; not currently wired

  // A staged file is removable if it's a newly-added file (present in the
  // caller-owned `files` prop) or an already-persisted image whose removal
  // the caller has opted into via `onRemoveExisting`.
  const isRemovable = (file: StagedFile) =>
    files.includes(file.file) || (Boolean(onRemoveExisting) && existingImageByFile.has(file.file));

  const handleRemove = (file: StagedFile) => {
    if (files.includes(file.file)) {
      onFilesChange(files.filter((f) => f !== file.file));
    } else {
      const existingImage = existingImageByFile.get(file.file);
      if (existingImage && onRemoveExisting) {
        onRemoveExisting(existingImage);
      } else {
        return;
      }
    }
    // Compare by the underlying File reference — `previewingFile` wraps a
    // freshly-built StagedFile each render, so wrapper identity never matches.
    if (previewingFile?.file === file.file) {
      setPreviewingFile(null);
    }
  };

  const renderActions = (file: StagedFile) => {
    return <FileActionsOverlay>
      <ActionIconButton
        size="large"
        onClick={() => handlePreview(file)}
        aria-label="Preview image"
      >
        <EditIcon />
      </ActionIconButton>
      {isRemovable(file) && (
        <ActionIconButton
          size="large"
          onClick={() => handleRemove(file)}
          aria-label="Remove image"
        >
          <Delete />
        </ActionIconButton>
      )}
    </FileActionsOverlay>
  };

  return (
    <RootContainer
      files={stagedFiles}
      onFilesAdded={handleFilesAdded}
      onValidationFailed={handleValidationFailed}
      acceptedFileTypes={ACCEPTED_FILE_TYPES}
      maxFiles={maxFiles}
      multiple={multiple}
    >
      <DropZone>
        <Gallery>
          <AddMoreButton label={DROP_PROMPT} />
          <FilePreviewGrid renderActions={renderActions} />
        </Gallery>
      </DropZone>
      {previewingFile && (
        <ImagePreviewModal
          file={previewingFile}
          // No `actions` passed: crop is not user-facing in the wizard (see
          // the note above `previewActions`). Preview-only mode, so
          // `onActionComplete` is omitted (defaults to a no-op) — it's
          // never reachable without `actions`.
          onClose={() => {
            previewingFileRef.current = null;
            setPreviewingFile(null);
          }}
        />
      )}
    </RootContainer>
  );
};

export default WizardImageUpload;
